import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useAuth } from '../hooks/useAuth';
import { WS_URL } from '../config';
import { Link } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode'; // Add this import

export default function Oneliner() {
  const { user, authToken, logout } = useAuth(); // Add logout to destructure
  const data = useWebSocket(WS_URL);
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [linkInfo, setLinkInfo] = useState({});
  const [sending, setSending] = useState(false); // Add this line
  const pendingFetches = useRef({});
  const inputRef = useRef();

  const emojiList = [
    '👍','👎','👌','👋','🤘','🖖','✌️','🤞','☝️','👇','🤝',
    '🙂','😀','😆','😂','😍','🥳','🤔','😢','😭','😠','👽',
    '❤️','⭐','☕','🍺','🍻','🍷','🥂','🍾','🍕','🍔','🪿',
    '🎧','🎶','🕺','💃','☀️','💦','❄️','💩','💀','🤖','🤡',
  ];

  // Custom image emojis
  const customEmojis = [
    { code: ':troll:', img: '/emoji/troll.png', alt: 'troll' },
    { code: ':allthethings:', img: '/emoji/allthethings.png', alt: 'allthethings' },
    { code: ':yey:', img: '/emoji/yey.png', alt: 'yey' },
    { code: ':yuno:', img: '/emoji/yuno.png', alt: 'yuno' },
    { code: ':jackie:', img: '/emoji/jackie.png', alt: 'jackie' },
    { code: ':farnsworth:', img: '/emoji/farnsworth.png', alt: 'farnsworth' },
  ];

  // Generate regex for custom emoji codes
  const customEmojiRegex = useMemo(() => {
    return new RegExp('(' + customEmojis.map(e => e.code.replace(/([.*+?^=!:${}()|[\]\\])/g, '\\$1')).join('|') + ')', 'g');
  }, [customEmojis]);

  const oneliner = useMemo(() => {
    if (!data || data.type !== 'playback_update') return [];
    return [...(data.oneliner || [])].sort((a, b) => b.timestamp - a.timestamp);
  }, [data]);

  // Helper to check token expiry
  const isTokenExpired = (token) => {
    try {
      const { exp } = jwtDecode(token);
      if (!exp) return true;
      return Date.now() >= exp * 1000;
    } catch {
      return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;

    // Check token expiry before sending
    if (!authToken || isTokenExpired(authToken)) {
      logout();
      return;
    }

    setSending(true);
    try {
      await fetch('/api/oneliner', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ message }),
      });
      setMessage('');
      setShowEmojiPicker(false);
      inputRef.current?.focus();
    } catch (err) {
      console.error('Failed to send oneliner:', err);
    } finally {
      setSending(false);
    }
  };

  const handleEmojiClick = (emoji) => {
    const el = inputRef.current;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newMessage = message.slice(0, start) + emoji + message.slice(end);
    setMessage(newMessage);
    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + emoji.length;
    }, 0);
  };

  const formatTimeAgo = (unixSeconds) => {
    const diffSeconds = Math.floor(Date.now() / 1000) - Math.floor(unixSeconds);
    if (diffSeconds < 60) return 'now';
    return `${Math.floor(diffSeconds / 60)} min`;
  };

  const fetchLinkInfo = async (type, id) => {
    const key = `${type}:${id}`;
    if (linkInfo[key] || pendingFetches.current[key]) return;
    pendingFetches.current[key] = true;
    try {
      const res = await fetch(`/api/${type}/${id}`);
      const json = await res.json();
      if (type === 'artist') {
        setLinkInfo((prev) => ({ ...prev, [key]: json.songs?.[0]?.song?.artist || 'Artist' }));
      } else if (type === 'song') {
        setLinkInfo((prev) => ({ ...prev, [key]: json.song?.title || 'Song' }));
      }
    } catch {
      setLinkInfo((prev) => ({ ...prev, [key]: type === 'artist' ? 'Artist' : 'Song' }));
    }
  };

  // Helper function to detect emoji characters
  const isEmoji = (char) => {
    // Check if the character is in our emojiList (fastest path)
    if (emojiList.includes(char)) return true;
    
    // Fallback to regex for other emojis not in our list
    const emojiRegex = /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{2B50}\u{2B55}\u{2934}-\u{2935}\u{3030}\u{303D}\u{3297}\u{3299}\u{FE00}-\u{FE0F}\u{1F9B0}-\u{1F9B3}]/u;
    return emojiRegex.test(char);
  };

  const renderMessageWithLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const artistRegex = /^https?:\/\/hypr\.website\/artist\/(\d+)$/;
    const songRegex = /^https?:\/\/hypr\.website\/song\/(\d+)$/;
    const parts = text.split(urlRegex);

    return parts.map((part, i) => {
      if (urlRegex.test(part)) {
        let match;
        if ((match = artistRegex.exec(part))) {
          const artistId = match[1];
          const key = `artist:${artistId}`;
          if (!linkInfo[key]) fetchLinkInfo('artist', artistId);
          return (
            <Link key={i} to={`/artist/${artistId}`} className="text-blue-600 underline">
              {linkInfo[key] || `Artist #${artistId}`}
            </Link>
          );
        }
        if ((match = songRegex.exec(part))) {
          const songId = match[1];
          const key = `song:${songId}`;
          if (!linkInfo[key]) fetchLinkInfo('song', songId);
          return (
            <Link key={i} to={`/song/${songId}`} className="text-blue-600 underline">
              {linkInfo[key] || `Song #${songId}`}
            </Link>
          );
        }
        return (
          <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
            {part}
          </a>
        );
      }

      // Process text for emojis and custom emoji codes
      let result = [];
      let buffer = '';
      
      // First, handle custom emoji codes
      let processedPart = part;
      let customEmojiResults = [];
      let lastCustomIndex = 0;
      let customMatch;
      
      while ((customMatch = customEmojiRegex.exec(part)) !== null) {
        if (customMatch.index > lastCustomIndex) {
          customEmojiResults.push({type: 'text', content: part.slice(lastCustomIndex, customMatch.index)});
        }
        
        const emoji = customEmojis.find(e => e.code === customMatch[0]);
        if (emoji) {
          customEmojiResults.push({
            type: 'customEmoji', 
            content: <img
              key={`custom-${i}-${customMatch.index}`}
              src={emoji.img}
              alt={emoji.alt}
              title={emoji.code}
              style={{ display: 'inline', width: '1.5em', height: '1.5em', verticalAlign: 'middle' }}
            />
          });
        } else {
          customEmojiResults.push({type: 'text', content: customMatch[0]});
        }
        lastCustomIndex = customMatch.index + customMatch[0].length;
      }
      
      if (lastCustomIndex < part.length) {
        customEmojiResults.push({type: 'text', content: part.slice(lastCustomIndex)});
      }
      
      // Then process standard emojis in each text segment
      return (
        <span key={i}>
          {customEmojiResults.map((item, j) => {
            if (item.type === 'customEmoji') return item.content;
            
            // Process standard emojis
            const chars = Array.from(item.content);
            return chars.map((char, k) => {
              if (isEmoji(char)) {
                return renderEmojiImage(char);
              }
              return char;
            });
          })}
        </span>
      );
    });
  };

  useEffect(() => {
    if (!sending && user && inputRef.current) {
      inputRef.current.focus();
    }
  }, [sending, user]);

  const renderEmojiImage = (emoji) => {
    // Handle multi-code point emojis (like heart emoji with variation selector)
    // Create a single emoji rendering rather than separate characters
    
    // Special cases first
    if (emoji === '❤️' || emoji === '❤') return (
      <img
        src="/emoji/2764.svg"
        alt={emoji}
        title={emoji}
        style={{ width: '1.5em', height: '1.5em', display: 'inline', verticalAlign: 'middle' }}
      />
    );
    if (emoji === '✌️') return (
      <img
        src="/emoji/270C.svg"
        alt={emoji}
        title={emoji}
        style={{ width: '1.5em', height: '1.5em', display: 'inline', verticalAlign: 'middle' }}
      />
    );
    if (emoji === '☝️') return (
      <img
        src="/emoji/261D.svg"
        alt={emoji}
        title={emoji}
        style={{ width: '1.5em', height: '1.5em', display: 'inline', verticalAlign: 'middle' }}
      />
    );
    
    // Handle different code points and formats
    const codePoints = [];
    for (const char of emoji) {
      const cp = char.codePointAt(0);
      // Skip variation selectors (FE0E-FE0F)
      if (cp < 0xFE00 || cp > 0xFE0F) {
        codePoints.push(cp.toString(16).toUpperCase());
      }
    }
    
    // Skip if we don't have any valid code points
    if (codePoints.length === 0) return emoji;
    
    const filename = codePoints.join('-');
    
    return (
      <img
        src={`/emoji/${filename}.svg`}
        alt={emoji}
        title={emoji}
        style={{ width: '1.5em', height: '1.5em', display: 'inline', verticalAlign: 'middle' }}
      />
    );
  };

  return (
    <>
      <div className="oneliner p-2 relative">
        <form onSubmit={handleSubmit} className="mb-2 relative flex">
          <input
            ref={inputRef}
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={user ? 'Say something...' : 'Log in to chat'}
            disabled={!user || sending} // Disable while sending
            maxLength={1024}
            className="w-full p-1 border border-gray-300 rounded text-sm"
          />
          <button
            type="button"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="ml-2 text-xl"
            disabled={!user || sending} // Disable for not logged in users or while sending
          >
            🙂
          </button>
        </form>

        {showEmojiPicker && user && ( // Only show emoji picker if user is logged in
          <div className="absolute z-10 bg-white border border-gray-300 rounded shadow p-2 max-w-xs w-fit text-lg flex flex-wrap gap-1">
            {emojiList.map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => handleEmojiClick(emoji)}
                className="hover:bg-gray-200 rounded px-1"
              >
                {renderEmojiImage(emoji)}
              </button>
            ))}
            {/* Custom image emojis */}
            {customEmojis.map((emoji) => (
              <button
                key={emoji.code}
                type="button"
                onClick={() => handleEmojiClick(emoji.code)}
                className="hover:bg-gray-200 rounded px-1"
                title={emoji.code}
              >
                <img src={emoji.img} alt={emoji.alt} style={{ width: '1.5em', height: '1.5em', display: 'inline', verticalAlign: 'middle' }} />
              </button>
            ))}
          </div>
        )}

        <div className="text-sm">
          {oneliner.map((line, index) => {
            const isMe = line.message.startsWith('/me ');
            const messageBody = isMe ? line.message.replace(/^\/me\s+/, '') : line.message;
            return (
              <div key={line.id}>
                {index !== 0 && <hr className="my-2 border-gray-300" />}
                <div className="flex justify-between items-center w-full text-gray-800 font-bold">
                  {!isMe && <span className="truncate">{line.author}</span>}
                  <span className="text-gray-500 font-normal text-xs text-right whitespace-nowrap ml-4 flex-1 text-right" style={{marginLeft:!isMe?"1rem":"auto"}}>
                    {formatTimeAgo(line.timestamp)}
                  </span>
                </div>
                <div className="text-gray-700 break-words">
                  {isMe ? (
                    <>
                      <span className="font-bold">{line.author}</span> {renderMessageWithLinks(messageBody)}
                    </>
                  ) : (
                    renderMessageWithLinks(messageBody)
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 text-center">
        {user && (
          <Link
            to="/onelinerhistory"
            className="text-blue-500 underline text-sm hover:text-blue-700"
          >
            Older messages
          </Link>
        )}
      </div>
    </>
  );
}
